/**
 * Events → Formats ("תבנית אירוע") — end-to-end live module view.
 *
 * Composes the live `/v1/contract/formats` envelope with the honesty
 * primitives (`<Freshness/>`, `<Denominator/>`, `<AnomalyChip/>`) and
 * the prototype's per-format comparison cards.
 *
 * Hidden formats: rows with `events === 0` are filtered out of the
 * comparison + KPIs; if any exist a single footer chip notes that they
 * were suppressed for this period (e.g. `rally` currently has 0 events
 * in live data).
 */
import type { Suite, ModuleSpec } from '../../../data/types/platform';
import { useEventsFormats } from '../../../data/queries/useEventsFormats';
import { FormatComparison, type FormatComparisonRow } from '../../../components/charts/FormatComparison';
import { AnomalyChip, Denominator, Freshness } from '../../../components/honesty';
import { Icon } from '../../../components/ui/Icon';
import { formatNumber } from '../../../utils/format';

export interface FormatsViewProps {
  system?: Suite;
  module?: ModuleSpec;
}

function toEpoch(iso: string): number {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : Date.now();
}

export function FormatsView({ system, module }: FormatsViewProps) {
  const { data, isLoading, error } = useEventsFormats();

  return (
    <article className="module-view formats-view module-page">
      <header className="mp-head">
        <div className="mp-eyebrow">
          <span>{system?.en ?? 'EVENTS'}</span>
          <span className="mp-sep">/</span>
          <span>{module?.he ?? 'תבנית אירוע'}</span>
        </div>
        <h1>{module?.he ?? 'תבנית אירוע'}</h1>
        <p className="mp-sub">
          {module?.sub ?? 'השוואת תבניות האירוע — נחשפו ← אישרו ← הגיעו, לפי תבנית.'}
        </p>
        <div className="mp-meta">
          {data && (
            <>
              <Freshness updatedAt={toEpoch(data.source_pulled_at)} />
              <span className="mp-dot" />
              <span>scope: {data.scope}</span>
              {data.meta.mock && (
                <>
                  <span className="mp-dot" />
                  <span>mock fixtures</span>
                </>
              )}
            </>
          )}
        </div>
      </header>

      <div className="mp-body">
        {isLoading && (
          <div className="mp-panel">
            <div className="mp-panel-body">
              <div className="mp-placeholder">
                <Icon name="loader" size={20} />
                <span>טוען נתוני תבניות…</span>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mp-panel">
            <div className="mp-panel-body">
              <div className="mp-placeholder">
                <Icon name="alert-triangle" size={20} />
                <span>שגיאה בטעינת המודול: {error.message}</span>
              </div>
            </div>
          </div>
        )}

        {data && <FormatsBody envelope={data} />}
      </div>
    </article>
  );
}

interface FormatsBodyProps {
  envelope: NonNullable<ReturnType<typeof useEventsFormats>['data']>;
}

function FormatsBody({ envelope }: FormatsBodyProps) {
  const allRows = envelope.data;

  // Derive: yes_show, then split active (events > 0) from inactive.
  // Inactive rows are suppressed from KPIs + the comparison panel so the
  // visual ordering by `events desc` is not polluted with empty cards.
  const enriched: FormatComparisonRow[] = allRows.map((r) => ({
    id: r.id,
    label: r.he,
    events: r.events,
    reached: r.reached,
    rsvp_yes: r.rsvp_yes,
    attended: r.attended,
    walk_in: r.walk_in,
    yes_show: r.rsvp_yes > 0 ? Math.max(0, r.attended - r.walk_in) / r.rsvp_yes : 0,
  }));
  const active = enriched.filter((r) => r.events > 0);
  const hidden = enriched.filter((r) => r.events === 0);
  const sorted = [...active].sort((a, b) => b.events - a.events);

  // KPI #2: "Best yes-show among formats with reached > 100" — the
  // threshold guards against trivially-high conversion on tiny denominators
  // (e.g. a format with 3 RSVPs and 3 attendees scoring 100%).
  const yesShowEligible = active.filter((r) => r.reached > 100);
  const bestYesShow = yesShowEligible.length
    ? yesShowEligible.reduce((best, r) => (r.yes_show > best.yes_show ? r : best))
    : null;

  const largest = active.length
    ? active.reduce((best, r) => (r.events > best.events ? r : best))
    : null;

  const avgAttendance = active.length
    ? active.reduce((sum, r) => sum + (r.events > 0 ? r.attended / r.events : 0), 0) /
      active.length
    : 0;

  const totalRecords = active.reduce((sum, r) => sum + r.reached, 0);
  const totalAttended = active.reduce((sum, r) => sum + r.attended, 0);

  return (
    <>
      {/* KPI strip — four-up, scoped to the period's active formats. */}
      <div className="kpi-grid cols-4">
        <KpiCell
          label="תבניות פעילות"
          value={formatNumber(active.length)}
          sub={`מתוך ${formatNumber(allRows.length)} תבניות בקטלוג`}
        />
        <KpiCell
          label="התבנית בעלת ההמרה הגבוהה"
          value={bestYesShow ? `${Math.round(bestYesShow.yes_show * 100)}%` : '—'}
          sub={
            bestYesShow
              ? `${bestYesShow.label} · אישור←הגעה`
              : 'אין תבנית עם 100+ פניות'
          }
          focus
        />
        <KpiCell
          label="התבנית הגדולה"
          value={largest ? formatNumber(largest.events) : '—'}
          sub={largest ? `${largest.label} · אירועים בתקופה` : 'אין תבניות פעילות'}
        />
        <KpiCell
          label="ממוצע הגעות לאירוע"
          value={formatNumber(Math.round(avgAttendance))}
          sub="ממוצע משוקלל בין תבניות פעילות"
          accent
        />
      </div>

      {/* Main comparison panel. */}
      <div className="panel">
        <div className="panel-head">
          <div className="title">השוואת תבניות · נחשפו ← אישרו ← הגיעו</div>
          <div className="sub">ממוצע משוקלל לפי תבנית · ממוין לפי כמות אירועים</div>
        </div>
        <div style={{ padding: '18px' }}>
          {sorted.length > 0 ? (
            <FormatComparison rows={sorted} />
          ) : (
            <div className="mp-placeholder">
              <Icon name="info" size={16} />
              <span>אין תבניות פעילות בתקופה זו.</span>
            </div>
          )}
          {sorted.length > 0 && (
            <div
              style={{
                marginTop: 14,
                paddingTop: 12,
                borderTop: '1px solid var(--hairline)',
                fontFamily: 'var(--font-sans)',
                fontSize: 11.5,
                color: 'var(--ink-3)',
                lineHeight: 1.5,
              }}
            >
              פורמטים שאין בהם רישום כניסה (כמו חוג בית או אירוע ווירטואלי)
              מציגים &quot;0 הגיעו&quot; — זה משקף את חוסר הרישום, לא היעדר
              משתתפים בפועל.
            </div>
          )}
        </div>
      </div>

      {/* Honesty footer. */}
      <footer
        className="mv-foot"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          padding: '16px 0 4px',
          fontFamily: 'var(--font-sans)',
          fontSize: 12,
          color: 'var(--ink-3)',
          alignItems: 'center',
        }}
      >
        <Denominator
          n={active.length}
          total={allRows.length}
          label="תבניות פעילות"
        />
        <Denominator
          n={totalAttended}
          total={totalRecords}
          label="הגיעו מתוך פניות"
        />
        <Freshness updatedAt={toEpoch(envelope.source_pulled_at)} />
        {hidden.length > 0 && (
          <span
            style={{
              padding: '4px 8px',
              border: '1px dashed var(--hairline-2, var(--hairline))',
              borderRadius: 4,
              color: 'var(--ink-3)',
            }}
            title={hidden.map((h) => h.label).join(' · ')}
          >
            פורמטים ללא פעילות בתקופה זו מוסתרים ({hidden.map((h) => h.label).join(' · ')})
          </span>
        )}
        {envelope.meta.anomalies.map((a) => (
          <AnomalyChip
            key={a.key}
            severity={a.severity === 'critical' ? 'alert' : a.severity}
            reason={a.message}
          />
        ))}
      </footer>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Local presentational helpers — mirror the FunnelView KpiCell.      */
/* ------------------------------------------------------------------ */

interface KpiCellProps {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
  focus?: boolean;
}

function KpiCell({ label, value, sub, accent, focus }: KpiCellProps) {
  const cls = ['kpi-cell', accent && 'accent', focus && 'focus']
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cls}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-meta">{sub}</div>}
    </div>
  );
}
